import { MCP_TOOL_GROUPS_IN_USE, type McpToolGroup } from "@esi/shared";
import type { BackofficeSection, SettingsGroup } from "../router.tsx";

/**
 * The `mcp.tools.<group>` switches (ADR 0018 §1), in the shared groups' order. A group with no
 * tools yet is not shown: its setting stays on the server, its switch appears with its first tool.
 */
export const MCP_TOOL_GROUP_KEYS = MCP_TOOL_GROUPS_IN_USE.map(
  (group) => `mcp.tools.${group}` as const,
);

/** `settings.behaviour.keys.<copy>` of a tool group's switch: `mcpToolsWiki`. */
export const mcpToolGroupCopy = (group: McpToolGroup) =>
  `mcpTools${group[0]?.toUpperCase()}${group.slice(1)}`;

/** A section of a settings page: its heading (`adminSettings.sections.<id>`) and its keys, in order. */
export interface SettingsGroupSection {
  id: SettingsSectionId;
  keys: readonly string[];
}

export type SettingsSectionId =
  | "answers"
  | "projects"
  | "signIn"
  | "mcpAccess"
  | "mcpToolGroups"
  | "directory"
  | "esiTeam"
  | "models"
  | "classification"
  | "files"
  | "wiki"
  | "memory"
  | "uploads"
  | "webSearch"
  | "webFetch"
  | "codeExecution"
  | "egress"
  | "other";

/**
 * The settings pages (spec `tenant-administration` "Settings administration with inheritance"):
 * each group's sections and keys, and the topic screens that own related settings. Grouped by
 * key, not by area: "Usage & limits" takes the size and scan limits out of the knowledge area.
 */
export const GROUP_SECTIONS: Readonly<Record<SettingsGroup, readonly SettingsGroupSection[]>> = {
  general: [
    {
      id: "answers",
      keys: [
        "answers.sourcesOnly",
        "conversations.standalone",
        "conversations.artefacts",
        "conversations.attachments",
        "conversations.thinkingSummary",
      ],
    },
    {
      id: "projects",
      keys: ["projects.allowPersonal", "projects.shareOutsideEntity", "projects.tasks"],
    },
  ],
  security: [
    {
      id: "signIn",
      keys: ["auth.jitProvisioning", "auth.mfa", "auth.nativeClients", "auth.deviceGrant"],
    },
    { id: "directory", keys: ["directory.idpGroupsMaxAge"] },
    {
      id: "esiTeam",
      keys: [
        "telemetry.clientEvents",
        "feedback.enabled",
        "feedback.agentSuggestions",
        "feedback.assistedTriage",
      ],
    },
  ],
  // ESI as an MCP server (mcp-server-core): access, then the tool groups (ADR 0018 §1).
  clients: [
    {
      id: "mcpAccess",
      keys: ["mcp.enabled", "mcp.writeTools", "wiki.mcpSelfPublish", "mcp.classificationCeiling"],
    },
    {
      id: "mcpToolGroups",
      keys: MCP_TOOL_GROUP_KEYS,
    },
  ],
  models: [
    {
      id: "models",
      keys: [
        "models.residency",
        "models.allowed",
        "models.defaultEffort",
        "models.advancedPicker",
        "models.turnMaxUsd",
        "models.sharePressureThresholdPct",
      ],
    },
  ],
  knowledge: [
    {
      id: "classification",
      keys: [
        "knowledge.classificationScheme",
        "knowledge.defaultClassification",
        "knowledge.declassification",
      ],
    },
    {
      id: "files",
      keys: ["knowledge.ocr", "knowledge.imageCaptions", "knowledge.generatedSources"],
    },
    {
      id: "wiki",
      keys: [
        "wiki.guidelines",
        "wiki.placement",
        "wiki.reviewAfterDays",
        "wiki.autoDraftSummaries",
        "wiki.suggestions",
        "wiki.trust",
      ],
    },
    { id: "memory", keys: ["memory.personal", "memory.project", "memory.retentionDays"] },
  ],
  limits: [
    {
      id: "uploads",
      keys: ["knowledge.maxUploadMb", "knowledge.ocrMaxPages", "knowledge.ocrMonthlyPages"],
    },
  ],
  integrations: [
    {
      id: "webSearch",
      keys: [
        "tools.webSearch",
        "tools.webSearch.provider",
        "tools.webSearch.domains",
        "tools.webSearch.maxAgeDays",
      ],
    },
    {
      id: "webFetch",
      keys: [
        "tools.webFetch",
        "tools.webFetch.provider",
        "tools.webFetch.approval",
        "tools.webFetch.domains",
      ],
    },
    {
      id: "codeExecution",
      keys: ["tools.codeExecution", "tools.codeExecution.provider", "tools.codeExecution.egress"],
    },
    // What outbound tools do after reading classified files, per audience (settings-typed-controls D10).
    { id: "egress", keys: ["tools.egress"] },
  ],
};

/** The backoffice screens that also hold settings of a group, linked from its page. */
export const GROUP_RELATED: Readonly<Record<SettingsGroup, readonly BackofficeSection[]>> = {
  general: ["branding"],
  security: ["directory", "signin"],
  clients: [],
  models: ["models", "residency"],
  knowledge: ["memory"],
  limits: ["plan"],
  integrations: [],
};

/** A grouped page's map: the page and section of each key, and a page's sections. */
export interface GroupMap<S extends string> {
  /** The page a key is on: a key no group names lands on General (under "Other"). */
  groupOf: (key: string) => SettingsGroup;
  /** The section a key is in on its page. */
  sectionOf: (key: string) => S | "other";
  /** The keys every group names, for the coverage check. */
  groupedKeys: () => string[];
  /** A group's sections over the keys given, with unmapped ones under "Other" on General. */
  sectionsOf: <T extends { key: string }>(
    group: SettingsGroup,
    settings: readonly T[],
  ) => { id: S | "other"; settings: T[] }[];
}

/** The map over a table of group sections (the backoffice's, or one that extends it). */
export function groupMap<S extends string>(
  table: Readonly<Record<SettingsGroup, readonly { id: S; keys: readonly string[] }[]>>,
): GroupMap<S> {
  const entries = Object.entries(table) as [
    SettingsGroup,
    readonly { id: S; keys: readonly string[] }[],
  ][];
  const groups = new Map<string, SettingsGroup>(
    entries.flatMap(([group, sections]) =>
      sections.flatMap((s) => s.keys.map((key) => [key, group] as const)),
    ),
  );
  const sectionIds = new Map<string, S>(
    entries.flatMap(([, sections]) =>
      sections.flatMap((s) => s.keys.map((key) => [key, s.id] as const)),
    ),
  );
  return {
    groupOf: (key) => groups.get(key) ?? "general",
    sectionOf: (key) => sectionIds.get(key) ?? "other",
    groupedKeys: () => [...groups.keys()],
    sectionsOf: (group, settings) => {
      const byKey = new Map(settings.map((s) => [s.key, s]));
      const sections: { id: S | "other"; settings: (typeof settings)[number][] }[] = table[
        group
      ].map((section) => ({
        id: section.id,
        settings: section.keys.flatMap((key) => {
          const found = byKey.get(key);
          return found ? [found] : [];
        }),
      }));
      if (group === "general") {
        sections.push({ id: "other", settings: settings.filter((s) => !groups.has(s.key)) });
      }
      return sections.filter((s) => s.settings.length > 0);
    },
  };
}

const BACKOFFICE = groupMap(GROUP_SECTIONS);

/** The page a key is on: a key no group names yet lands on General (under "Other"). */
export const groupOf = BACKOFFICE.groupOf;

/** The section a key is in on its page. */
export const sectionOf = BACKOFFICE.sectionOf;

/** The keys every group names, for the coverage check. */
export const groupedKeys = BACKOFFICE.groupedKeys;

/** A group's sections over the keys the catalogue holds, with unmapped ones under "Other". */
export const sectionsOf = BACKOFFICE.sectionsOf;
